import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A drag-to-resize vertical divider.
 *
 * The tessa Studio rails this shell is modelled on are FIXED width, which is fine for
 * a guided flow but wrong for a terminal: the whole point is that the operator decides
 * how much room the ranking, the chart, and the evidence each get. Widths persist to
 * localStorage so a reload does not undo that decision.
 *
 * Pointer events (not mouse) so it works with a trackpad, pen, or touch, and pointer
 * capture keeps the drag alive when the cursor outruns the 4px handle.
 */
export function useResizable(key: string, initial: number, min: number, max: number) {
  const [size, setSize] = useState<number>(() => {
    const saved = localStorage.getItem(`srank.pane.${key}`);
    const n = saved ? Number(saved) : NaN;
    return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : initial;
  });

  useEffect(() => {
    localStorage.setItem(`srank.pane.${key}`, String(size));
  }, [key, size]);

  return [size, setSize, { min, max }] as const;
}

export function Divider({
  onResize,
  side,
}: {
  onResize: (deltaPx: number) => void;
  /** Which pane the divider belongs to, so the drag direction reads correctly. */
  side: "left" | "right";
}) {
  const last = useRef<number | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    last.current = e.clientX;
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (last.current === null) return;
      const delta = e.clientX - last.current;
      last.current = e.clientX;
      onResize(side === "left" ? delta : -delta);
    },
    [onResize, side],
  );

  const end = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    last.current = null;
  }, []);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      className="w-1 shrink-0 cursor-col-resize bg-term-edge/60 hover:bg-term-accent/70 transition-colors"
    />
  );
}
